import React, { FC, useEffect, useState } from "react";
import { t, formatDate } from "utils/i18n";
import { useGetComponentState } from "composants/GetComponent";
import { GSBuilder, GSVisitorCallback, initRsqlCriteria } from "utils/query.utils";
import { findAll, findOne } from "api";
import { Pojo } from "types/Galaxy";
import Axios from "axios";
import { showContextInfo } from "api/contextMenu";

interface ContextInfoProps {
  contextColumnName: string | null;
  contextTableName: string | null;
  origineTableId: string | null;
  contextTableId: string | null;
}

const visitorToJSX: GSVisitorCallback<React.ReactNode> = {
  And: (node, visitor) => {
    let nodes = [];
    for (let n of node.nodes) {
      nodes.push(GSBuilder.visit(n, visitor));
    }
    return <ul>{nodes}</ul>;
  },
  Or: (node, visitor) => {
    let nodes = [];
    for (let n of node.nodes) {
      nodes.push(GSBuilder.visit(n, visitor));
    }
    return <ul>{nodes}</ul>;
  },
  Comparison: node => {
    return (
      <li key={node.field}>
        {node.field} {node.value ? <strong>{node.value}</strong> : null}
      </li>
    );
  }
};

function formatDateSuivi(date: string) {
  return formatDate(date, {
    day: "numeric",
    month: "long",
    year: "numeric"
  });
}

const ContextInfo: FC<ContextInfoProps> = props => {
  const [help, setHelp] = useState("");
  const [currentEntity, setCurrentEntity] = useState<Pojo | null>(null);
  const [historique, setHistorique] = useState<Pojo[]>([]);
  const [tableId, setTableId] = useState<string | null>(null);
  const [columnId, setColumnId] = useState<string | null>(null);

  const { state, computedColumnsState } = useGetComponentState("CTX_INFO", props.contextTableName, [
    props.contextColumnName ?? ""
  ]);

  useEffect(() => {
    if (props.contextColumnName && props.contextTableName) {
      showContextInfo(props.contextColumnName, props.contextTableName)
        .then(res => {
          setHelp(res.data);
        })
        .catch(err => {
          console.groupCollapsed("erreur lors de la récupération de l'aide'");
          console.error(err);
          console.groupEnd();
        });
    }
  }, [props.contextColumnName, props.contextTableName]);

  useEffect(() => {
    if (props.contextTableName) {
      const rsqlTable = initRsqlCriteria();
      rsqlTable.filters.and(
        GSBuilder.toFilter(GSBuilder.Comparison("sjtaName", "OPER_EQ", props.contextTableName))
      );
      findAll({
        tableName: "syjTables",
        filter: rsqlTable.build()
      }).then(res => {
        if (res.data.data.length > 0) {
          setTableId(res.data.data[0].id);
        }
      });
    }
  }, [props.contextTableName]);

  useEffect(() => {
    if (tableId && props.contextColumnName) {
      const rsqlColumn = initRsqlCriteria();
      const node = GSBuilder.AND(
        GSBuilder.Comparison("sjcoColumn", "OPER_EQ", props.contextColumnName),
        GSBuilder.Comparison("syjTablesId.id", "OPER_EQ", tableId)
      );
      rsqlColumn.filters.and(GSBuilder.toFilter(node));
      findAll({
        tableName: "syjColumn",
        filter: rsqlColumn.build()
      }).then(res => {
        if (res.data.data.length > 0) {
          setColumnId(res.data.data[0].id);
        }
      });
    }
  }, [tableId, props.contextColumnName]);

  useEffect(() => {
    if (currentEntity?.id && columnId) {
      const node = GSBuilder.AND(
        GSBuilder.Comparison("smcoEntityId", "OPER_EQ", currentEntity.id),
        GSBuilder.Comparison("syjColumnId.id", "OPER_EQ", columnId)
      );
      const rsqlSuivi = initRsqlCriteria();
      rsqlSuivi.filters.and(GSBuilder.toFilter(node));

      findAll({
        tableName: "suiviModifSyjColumn",
        filter: rsqlSuivi.build()
      }).then(res => {
        let a = res.data.data;
        const sortedData = a.sort(
          (a, b) => new Date(b.smcoDtSuivi).getTime() - new Date(a.smcoDtSuivi).getTime()
        );
        setHistorique(sortedData);
      });
    }
  }, [currentEntity, columnId]);

  useEffect(() => {
    if (!props.contextTableName || !props.origineTableId) return;

    const source = Axios.CancelToken.source();

    findOne({ tableName: props.contextTableName, id: props.origineTableId }, source)
      .then(res => setCurrentEntity(res.data))
      .catch(err => {
        console.error(
          `error during fetch of ${props.contextTableName} with id ${props.origineTableId}`
        );
        setCurrentEntity(null);
      });

    return () => {
      source.cancel();
    };
  }, [props.contextTableName, props.origineTableId]);

  return (
    <div>
      <div className="field is-horizontal">
        <div className="field-label is-normal has-text-left">
          <label className="label">{t("commun_table")}</label>
        </div>

        <div className="field-body">
          <div className="field">
            <div className="control">
              <output className="input">{props.contextTableName}</output>
            </div>
          </div>
        </div>
      </div>
      <div className="field is-horizontal">
        <div className="field-label is-normal has-text-left">
          <label className="label">{t("commun_colonne")}</label>
        </div>

        <div className="field-body">
          <div className="field">
            <div className="control">
              <output className="input">{props.contextColumnName}</output>
            </div>
          </div>
        </div>
      </div>
      {state === "OK" &&
        computedColumnsState.columns.length > 0 &&
        computedColumnsState.columns[0].typeCompo === "GS" && (
          <div className="field is-horizontal">
            <div className="field-label is-normal has-text-left">
              <label className="label">{t("commun_recherche")}</label>
            </div>
            <div className="field-body">
              <div className="field">
                <div className="control">
                  <div className="container content">
                    {GSBuilder.visit(
                      computedColumnsState.columns[0].additionalClause,
                      visitorToJSX
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

      <div className="field is-horizontal">
        <div className="field-label is-normal has-text-left">
          <label className="label">{t("commun_suivi_modification")}</label>
        </div>
        <div className="field-body">
          {currentEntity && (
            <ul>
              {currentEntity.dtMaj && (
                <li className="mb-5">
                  {t("commun_suivi_modification_maj", {
                    date: currentEntity.dtMaj,
                    user: currentEntity.loginMaj
                  })}
                </li>
              )}
              <li>
                {t("commun_suivi_modification_cre", {
                  date: currentEntity.dtCre,
                  user: currentEntity.loginCre
                })}
              </li>
            </ul>
          )}
        </div>
      </div>
      {historique && historique.length > 0 && (
        <div className="field is-horizontal">
          <div className="field-label is-normal has-text-left">
            <label className="label">{t("commun_historiquemodif")}</label>
          </div>
          <div className="field-body">
            <ul className="pt-7">
              {historique.map((modif, index) => (
                <li key={modif.id}>
                  <div className="is-flex is-align-items-center">
                    <span className="has-text-weight">
                      <span className="has-text-danger has-text-weight-bold">
                        {modif.smcoValOld}
                      </span>
                      <span className="mx-2 has-text-grey">→</span>
                      <span className="has-text-success has-text-weight-bold">
                        {modif.smcoValNew}
                      </span>
                      &nbsp; &nbsp;
                      {t("commun_suivi_modification_maj", {
                        date: modif.smcoDtSuivi,
                        user: modif.smcoLogin
                      })}
                    </span>
                  </div>
                </li>
              ))}
            </ul>
          </div>
        </div>
      )}
      <pre className="p-8">{help}</pre>
    </div>
  );
};

export default ContextInfo;
